/**
 * Full document detail shell for the HERO Dokumente module (header + tabs + preview).
 */
import { ld } from "@/lib/theme/light-dark";
import { ChevronLeft, FileText, Lock, Pencil, Send } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { DocItem } from "./module-dokumente-data";
import { getDetailFixture } from "./module-dokumente-data";
import { DokumenteDetailTabs } from "./module-dokumente-detail-tabs";
import { DokumentePreview } from "./module-dokumente-previews";

const HEADER_CONTROL: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  height: 28,
  padding: "0 8px",
  borderRadius: 6,
  border: "none",
  background: "transparent",
  fontFamily: H.sans,
  fontSize: 11,
  fontWeight: 500,
  color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
};

function HeaderAction(props: {
  readonly icon: ReactNode;
  readonly label: string;
}): ReactNode {
  return (
    <span style={{ ...HEADER_CONTROL, cursor: "default" }}>
      {props.icon}
      <span style={{ whiteSpace: "nowrap" }}>{props.label}</span>
    </span>
  );
}

export function DokumenteDetail(props: {
  readonly doc: DocItem;
  readonly onBack: () => void;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumenteDetail");
  const tData = useTranslations("marketing.moduleDokumenteData");
  const fixture = getDetailFixture(tData, props.doc);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: MODULE_HEIGHT,
        fontFamily: H.sans,
        minWidth: 0,
        flex: 1,
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 16px",
          borderBottom: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={props.onBack}
          aria-label={t("zurueckUebersicht")}
          style={{
            ...HEADER_CONTROL,
            cursor: "pointer",
            width: 28,
            padding: 0,
            justifyContent: "center",
          }}
        >
          <ChevronLeft width={18} height={18} strokeWidth={1.9} />
        </button>
        <span
          style={{ color: H.inkDim, display: "inline-flex", flexShrink: 0 }}
        >
          <FileText width={16} height={16} strokeWidth={1.8} />
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 13.5,
            fontWeight: 600,
            color: H.ink,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {fixture.title}
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <HeaderAction
            icon={<Send width={14} height={14} strokeWidth={1.8} />}
            label={t("weiterleiten")}
          />
          <HeaderAction
            icon={<Lock width={14} height={14} strokeWidth={1.8} />}
            label={t("zugriffFreigabe")}
          />
          <HeaderAction
            icon={<Pencil width={14} height={14} strokeWidth={1.8} />}
            label={t("bearbeiten")}
          />
        </div>
      </header>

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <DokumenteDetailTabs key={props.doc.id} fixture={fixture} />
        <DokumentePreview
          kind={props.doc.previewKind}
          imageLabel={fixture.title}
        />
      </div>
    </div>
  );
}
